import type { Country, CountryGroup } from '../lib/types';

/**
 * Region list builder for the customizer wizard.
 *
 * Ported from the Deploy module's `countries` memo
 * (`src/components/modules/deploy/index.tsx`, ~line 52): it is **API-driven** —
 * it walks the active `operating_region_groups` (priority-sorted) and, per group,
 * its `operating_regions` (priority-sorted, matched by
 * `maps_service_identifier.operating_region_ids`) — so the FULL operating-region
 * list is included, not a hardcoded subset. `comingSoon` is read from
 * `operating_regions[].art_bundle[0].properties.comingSoon`.
 *
 * Local to this page on purpose — does NOT reuse the configurator-v3 helpers.
 */

interface RawArtBundle {
  text_name?: Record<string, string>;
  logo?: string;
  properties?: { comingSoon?: boolean };
}

interface RawRegion {
  code: string;
  is_active?: boolean;
  priority?: number;
  currency?: { currency_code?: string };
  region_data?: { currency_code?: string };
  art_bundle?: RawArtBundle[];
}

interface RawRegionGroup {
  is_active?: boolean;
  priority?: number;
  maps_service_identifier?: { operating_region_ids?: string[] };
  art_bundle?: RawArtBundle[];
}

interface RawRegionConfig {
  operating_region_groups?: RawRegionGroup[];
  operating_regions?: RawRegion[];
}

/** Resolve a localized text map to a display string (current lang → en → first). */
function loc(map: Record<string, string> | undefined, lang: string): string {
  if (!map) return '';
  return map[lang] ?? map.en ?? Object.values(map)[0] ?? '';
}

/** Drop a trailing currency hint, e.g. "International ($)" → "International". */
const stripCurrencyHint = (name: string): string => name.replace(/\s*\(.*\)\s*$/, '').trim() || name;

export function buildRegions(
  config: unknown,
  lang = 'en',
): { countries: Country[]; groups: CountryGroup[] } {
  const cfg = (config ?? {}) as RawRegionConfig;
  const regions = cfg.operating_regions ?? [];

  // A region is live/selectable by its own `is_active`; `false` = not live yet (shown but
  // not selectable). Order live markets ahead of not-yet-live ones, then by CMS priority.
  const isRegionLive = (r: RawRegion): boolean => r.is_active !== false;
  const regionOrder = (a: RawRegion, b: RawRegion): number =>
    Number(isRegionLive(b)) - Number(isRegionLive(a)) || (a.priority ?? 0) - (b.priority ?? 0);

  const ordered = (cfg.operating_region_groups ?? [])
    .filter((g) => g.is_active)
    .map((group) => {
      const ids = group.maps_service_identifier?.operating_region_ids ?? [];
      const groupRegions = regions.filter((r) => ids.includes(r.code)).slice().sort(regionOrder);
      return { group, regions: groupRegions };
    })
    .slice()
    // Groups holding at least one live market first, then by CMS priority, so live markets
    // sit at the top of the dropdown and not-yet-live ones follow.
    .sort((a, b) => {
      const aLive = a.regions.some(isRegionLive) ? 0 : 1;
      const bLive = b.regions.some(isRegionLive) ? 0 : 1;
      return aLive - bLive || (a.group.priority ?? 0) - (b.group.priority ?? 0);
    });

  const countries: Country[] = [];
  const groups: CountryGroup[] = [];

  for (const { group, regions: groupRegions } of ordered) {
    const cs: Country[] = groupRegions.map((r) => {
      const ab = r.art_bundle?.[0];
      return {
        code: r.code,
        name: stripCurrencyHint(loc(ab?.text_name, lang) || r.code),
        currency: r.currency?.currency_code ?? r.region_data?.currency_code ?? '',
        flag: ab?.logo ?? '',
        // Availability signal: the region's own `is_active`.
        isActive: isRegionLive(r),
        // Decorative only (art bundles are decorative): drives the "Soon" pill, nothing else.
        comingSoon: ab?.properties?.comingSoon ?? false,
      };
    });
    if (cs.length === 0) continue;
    countries.push(...cs);
    groups.push({ name: stripCurrencyHint(loc(group.art_bundle?.[0]?.text_name, lang)), countries: cs });
  }

  return { countries, groups };
}